import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAssets } from '@/hooks/useAssets';
import { formatRelativeDate } from '@/utils/date';
import type { Asset } from '@/types/api';
import { AssetBinaryImage } from '@/components/domain/AssetBinaryPreview';
import { Icon } from '@/components/primitives/Icon';
import { Button } from '@/components/primitives/Button';
import { IconButton } from '@/components/primitives/IconButton';
import { Chip } from '@/components/primitives/Chip';
import { Skeleton } from '@/components/primitives/Skeleton';
import { UploadAssetModal } from '@/components/modals/UploadAssetModal';
import { downloadAssetContentFile, uploadAssetWithProgress } from '@/services/assets';

type FilterTab = 'All Assets' | 'Images' | 'Videos';
type ViewMode = 'list' | 'grid';

const FILTER_TABS: FilterTab[] = ['All Assets', 'Images', 'Videos'];

function assetMimeType(asset: Asset): string {
  const ext = asset.name.split('.').pop()?.toUpperCase() ?? '';
  return ext || asset.asset_type;
}

function assetCategory(asset: Asset): 'Image' | 'Video' {
  return asset.asset_type === 'video' ? 'Video' : 'Image';
}

function AssetThumb({ asset }: { asset: Asset }) {
  const endpoint = asset.thumbnail_url ? 'thumbnail' : 'content';
  const hint = asset.thumbnail_url ?? asset.uri;

  if (asset.asset_type === 'video') {
    return (
      <div className="w-12 h-12 shrink-0 bg-stone-900 rounded-lg overflow-hidden relative flex items-center justify-center">
        {asset.thumbnail_url && asset.id != null && (
          <AssetBinaryImage
            assetId={asset.id}
            endpoint="thumbnail"
            remoteUrlHint={asset.thumbnail_url}
            alt={asset.alt_text || asset.name}
            className="w-full h-full object-cover opacity-60"
            loadingClassName="w-full h-full flex items-center justify-center bg-stone-900"
          />
        )}
        <div className="absolute inset-0 flex items-center justify-center">
          <Icon name="play_arrow" filled className="text-white text-sm" />
        </div>
      </div>
    );
  }

  return (
    <div className="w-12 h-12 shrink-0 bg-surface-container-low rounded-lg overflow-hidden">
      {asset.id != null ? (
        <AssetBinaryImage
          assetId={asset.id}
          endpoint={endpoint}
          remoteUrlHint={hint}
          alt={asset.alt_text || asset.name}
          className="w-full h-full object-cover"
          loadingClassName="w-full h-full flex items-center justify-center bg-surface-container-low"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <Icon name="image" className="text-on-surface-variant/40" />
        </div>
      )}
    </div>
  );
}

function GridThumb({ asset }: { asset: Asset }) {
  const endpoint = asset.thumbnail_url ? 'thumbnail' : 'content';
  const hint = asset.thumbnail_url ?? asset.uri;

  const card = (
    <div className="group bg-surface-container-lowest rounded-2xl border border-outline-variant/10 overflow-hidden hover:shadow-md transition-all">
      <div className="h-32 relative bg-surface-container-low flex items-center justify-center overflow-hidden">
        {asset.asset_type === 'video' && !asset.thumbnail_url ? (
          <Icon name="videocam" className="text-on-surface-variant/30 text-4xl" />
        ) : asset.id != null ? (
          <AssetBinaryImage
            assetId={asset.id}
            endpoint={endpoint}
            remoteUrlHint={hint}
            alt={asset.alt_text || asset.name}
            className="w-full h-full object-cover"
            loadingClassName="w-full h-full flex items-center justify-center bg-surface-container-low"
          />
        ) : (
          <Icon
            name={asset.asset_type === 'video' ? 'videocam' : 'image'}
            className="text-on-surface-variant/30 text-4xl"
          />
        )}
        {asset.asset_type === 'video' && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
            <Icon name="play_circle" filled className="text-white text-4xl" />
          </div>
        )}
      </div>
      <div className="p-3">
        <h4 className="text-xs font-bold text-on-surface truncate">{asset.name}</h4>
        <p className="text-[10px] text-on-surface-variant mt-0.5">{assetMimeType(asset)}</p>
      </div>
    </div>
  );

  if (asset.id == null) {
    return card;
  }

  return (
    <Link to={`/assets/${asset.id}`} className="block">
      {card}
    </Link>
  );
}

export default function AssetsPage() {
  const [activeFilter, setActiveFilter] = useState<FilterTab>('All Assets');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState('');

  const { data, loading, error, refetch } = useAssets();

  const filtered = (data ?? []).filter((a) => {
    if (activeFilter === 'All Assets') return true;
    if (activeFilter === 'Images') return a.asset_type === 'image';
    if (activeFilter === 'Videos') return a.asset_type === 'video';
    return true;
  });

  async function handleAssetUpload(file: File, name: string) {
    setIsUploadModalOpen(false);
    setUploadError('');
    setUploadProgress(0);
    try {
      await uploadAssetWithProgress({ file, name }, (pct) => {
        setUploadProgress(pct);
      });
      refetch();
    } catch (err: unknown) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploadProgress(null);
    }
  }

  async function handleAssetDownload(asset: Asset) {
    if (asset.id == null) return;
    setUploadError('');
    try {
      await downloadAssetContentFile(asset.id, asset.name);
    } catch (err: unknown) {
      setUploadError(err instanceof Error ? err.message : 'Download failed');
    }
  }

  return (
    <div className="p-4 md:p-8 lg:p-12 space-y-10">
      {/* Header */}
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <h1 className="text-4xl font-black font-headline tracking-tight text-on-surface">
            Asset Library
          </h1>
          <p className="text-on-surface-variant mt-1">
            Manage and organise your digital production assets.
          </p>
        </div>
        <div className="flex items-center gap-4">
          {/* Progress Circular Upload Bar */}
          {uploadProgress !== null && (
            <div className="flex items-center gap-3 bg-surface-container-low px-3 py-1.5 rounded-full border border-outline-variant/10">
              <div className="relative flex items-center justify-center w-8 h-8">
                <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
                  <circle
                    className="stroke-surface-container-highest"
                    cx="18"
                    cy="18"
                    fill="none"
                    r="16"
                    strokeWidth="3"
                  />
                  <circle
                    className="stroke-primary transition-all duration-300 ease-out"
                    cx="18"
                    cy="18"
                    fill="none"
                    r="16"
                    strokeWidth="3"
                    strokeDasharray="100"
                    strokeDashoffset={100 - uploadProgress}
                    strokeLinecap="round"
                  />
                </svg>
                <span className="absolute text-[9px] font-black text-on-surface tabular-nums tracking-tighter">
                  {uploadProgress}%
                </span>
              </div>
            </div>
          )}

          {/* Upload button */}
          <Button onClick={() => setIsUploadModalOpen(true)} disabled={uploadProgress !== null}>
            <Icon name="upload_file" className="text-lg" />
            {uploadProgress !== null ? 'Uploading...' : 'Upload Asset'}
          </Button>
        </div>
      </div>

      {/* Upload error */}
      {uploadError && (
        <div className="p-4 rounded-xl bg-error-container text-on-error-container text-sm">
          {uploadError}
        </div>
      )}

      {/* Fetch error */}
      {error && (
        <div className="p-4 rounded-xl bg-error-container text-on-error-container text-sm">
          {error}
        </div>
      )}

      {/* Filter bar */}
      <div className="flex items-center justify-between py-4 border-y border-outline-variant/10">
        <div className="flex items-center gap-2">
          {FILTER_TABS.map((tab) => (
            <Chip key={tab} active={activeFilter === tab} onClick={() => setActiveFilter(tab)}>
              {tab}
            </Chip>
          ))}
          {data && (
            <span className="ml-2 text-xs text-on-surface-variant">
              {filtered.length} asset{filtered.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {/* View toggle */}
        <div className="flex bg-surface-container-low p-1 rounded-lg">
          <IconButton
            icon="grid_view"
            size="sm"
            onClick={() => setViewMode('grid')}
            className={
              viewMode === 'grid'
                ? 'bg-surface-container-lowest shadow-sm text-primary'
                : 'text-on-surface-variant hover:text-on-surface'
            }
          />
          <IconButton
            icon="list"
            size="sm"
            onClick={() => setViewMode('list')}
            className={
              viewMode === 'list'
                ? 'bg-surface-container-lowest shadow-sm text-primary'
                : 'text-on-surface-variant hover:text-on-surface'
            }
          />
        </div>
      </div>

      {/* Loading skeletons */}
      {loading && (
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} rounded="xl" className="h-16" />
          ))}
        </div>
      )}

      {/* List view */}
      {!loading && viewMode === 'list' && (
        <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
          <div className="space-y-1 min-w-[700px]">
            <div className="grid grid-cols-[1fr_100px_80px_80px_120px_48px] gap-4 px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-on-surface-variant/60">
              <div>Name</div>
              <div>Category</div>
              <div>Type</div>
              <div>Modified</div>
              <div className="text-right">Actions</div>
              <div />
            </div>
            {filtered.map((asset) => (
              <div
                key={asset.id}
                className="group grid grid-cols-[1fr_100px_80px_80px_120px_48px] gap-4 items-center px-6 py-4 bg-surface-container-lowest border border-outline-variant/10 rounded-xl hover:shadow-md transition-all mb-2"
              >
                <div className="flex items-center gap-4 overflow-hidden">
                  <AssetThumb asset={asset} />
                  <div className="min-w-0">
                    <h4 className="text-sm font-bold text-on-surface truncate">{asset.name}</h4>
                    <p className="text-[10px] text-on-surface-variant">{asset.alt_text || '—'}</p>
                  </div>
                </div>
                <div className="text-xs font-medium text-on-surface-variant">
                  {assetCategory(asset)}
                </div>
                <div className="text-xs font-medium text-on-surface-variant">
                  {assetMimeType(asset)}
                </div>
                <div className="text-xs font-medium text-on-surface-variant">
                  {formatRelativeDate(asset.updated_at)}
                </div>
                <div className="flex items-center justify-end gap-3">
                  {asset.id != null ? (
                    <Link
                      to={`/assets/${asset.id}`}
                      className="text-xs font-bold text-primary hover:underline cursor-pointer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Preview
                    </Link>
                  ) : (
                    <span className="text-xs font-bold text-on-surface-variant/60">Preview</span>
                  )}
                  <button
                    type="button"
                    className="w-8 h-8 flex items-center justify-center rounded-full border border-outline-variant/30 text-on-surface-variant hover:bg-primary/5 hover:text-primary transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleAssetDownload(asset);
                    }}
                    disabled={asset.id == null}
                  >
                    <Icon name="download" className="text-lg" />
                  </button>
                </div>
                <IconButton icon="more_vert" size="lg" className="hover:bg-surface-container" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Grid view */}
      {!loading && viewMode === 'grid' && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {filtered.map((asset) => (
            <GridThumb key={asset.id} asset={asset} />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && filtered.length === 0 && !error && (
        <div className="py-24 text-center">
          <Icon name="folder_open" className="text-6xl text-primary/30" />
          <p className="mt-4 text-on-surface-variant font-medium">No assets yet.</p>
          <p className="text-sm text-on-surface-variant mt-1">
            Upload logos, images, or videos to get started.
          </p>
        </div>
      )}

      <UploadAssetModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onConfirm={handleAssetUpload}
      />
    </div>
  );
}
